import React from 'react'
import axios from 'axios'
import get from 'lodash.get'
import moment from 'moment'
import GatsbyConfig from '../../../gatsby-config.js'
import {
  getEl,
  formatPrice,
  scrollDistanceToTop,
} from '../../utils/app-helpers'
import AddNightsSummary from './add-nights-summary'

const { siteMetadata: config } = GatsbyConfig

export default class AddNights extends React.Component {
  componentWillMount() {
    this.state = { nightsBefore: 0, nightsAfter: 0, isBusy: false, error: '' }
  }

  componentDidMount() {
    this.stripeWatch = setInterval(this.stripeWatchInterval, 200)
  }

  stripeWatchInterval = () => {
    const cardEl = getEl('#card-element')

    if (cardEl && window.Stripe) {
      this.stripe = window.Stripe(process.env.GATSBY_STRIPE_PUBLIC_KEY)
      this.elements = this.stripe.elements(cardEl)
      this.card = this.elements.create('card', { style: config.stripe.style })
      this.card.mount('#card-element')
      this.card.addEventListener('change', this.onStripeChange)
      clearInterval(this.stripeWatch)
    }
  }

  onStripeChange = ({ error }) => {
    this.setState({ error: get(error, 'message', '') })
  }

  onChangeNightsBefore = proxy => {
    this.setState({ nightsBefore: parseInt(proxy.target.value, 10) })
  }

  onChangeNightsAfter = proxy => {
    this.setState({ nightsAfter: parseInt(proxy.target.value, 10) })
  }

  onSubmit = e => {
    e.preventDefault()
    e.stopPropagation()

    const { stripe, card } = this
    const { nightsBefore, nightsAfter } = this.state

    if (!stripe) {
      this.setState({ error: defaultErr })
      return
    }
    this.setState({ isBusy: true })

    window.scrollTo(0, scrollDistanceToTop(this.refs.formFrame))
    stripe
      .createToken(card)
      .then(this.onTokenSuccess)
      .catch(this.onTokenError)
  }

  onTokenSuccess = ({ token, error }) => {
    const errorMsg = get(error, 'message', '')
    if (errorMsg) {
      return Promise.reject(errorMsg)
    }
    return this.stripeTokenHandler(token)
  }

  onTokenError = (errorMsg = 'Unable to submit payment.') => {
    this.setState({ isBusy: false, errorMsg })
  }

  stripeTokenHandler(token) {
    const { trip, bookData, subscriptionId } = this.props
    const nightsBefore = get(this, 'state.nightsBefore', 0)
    const nightsAfter = get(this, 'state.nightsAfter', 0)
    const dates = this.getDates()
    const nights = []
    dates.before.forEach((date, d) => {
      if (d < nightsBefore) nights.unshift(date)
    })
    dates.after.forEach((date, d) => {
      if (d < nightsAfter) nights.push(date)
    })
    const { room, primaryName, spouseName } = bookData
    const tripData = config.trips[trip]
    const roomData = tripData.rooms[room]
    const pricePerNightInCents =
      get(roomData, 'extraNightPrices.default', 0) * 100
    const amount = (nightsBefore + nightsAfter) * pricePerNightInCents
    const newCheckInDate = moment(tripData.checkInDate, 'MMM D, YYYY').subtract(
      'day',
      nightsBefore
    )
    const newCheckOutDate = moment(tripData.checkOutDate, 'MMM D, YYYY').add(
      'day',
      nightsAfter
    )
    const params = {
      email: bookData.email,
      source: token.id,
      amount,
      description: `Additional Nights: ${nights.join(', ')}`,
      subscriptionId,
      trip,
      room,
      tripName: tripData.name,
      tripYear: tripData.year,
      roomName: roomData.name,
      nightsBefore,
      nightsAfter,
      emaildata: JSON.stringify({
        primaryName,
        spouseName,
        nightsBefore,
        nightsAfter,
        nights: nights.join(', '),
        tripDates: tripData.dates,
        tripStartTime: tripData.startTime,
        tripName: tripData.name,
        tripYear: tripData.year,
        tripRegion: tripData.region,
        tripCountry: tripData.country,
        tripVenue: tripData.venue,
        checkInDay: tripData.checkInDay,
        checkInDate: tripData.checkInDate,
        checkInTime: tripData.checkInTime,
        checkOutDay: tripData.checkOutDay,
        checkOutDate: tripData.checkOutDate,
        checkOutTime: tripData.checkOutTime,
        arrivePriorTo: tripData.arrivePriorTo,
        roomName: roomData.name,
        checkOutTime: tripData.checkOutTime,
        departureAirport: tripData.departureAirport,
        departureAirCode: tripData.departureAirCode,
        amount: formatPrice(amount),
        newCheckInDate: newCheckInDate.format('MMMM D, YYYY'),
        newCheckOutDate: newCheckOutDate.format('MMMM D, YYYY'),
      }),
    }
    let keyPairs = []
    for (let key in params) {
      keyPairs.push(`${key}=${encodeURIComponent(params[key])}`)
    }

    return axios
      .post(`${process.env.GATSBY_ENDPOINT_ADD_NIGHTS}?${keyPairs.join('&')}`, {
        headers: config.axiosHeaders,
      })
      .then(this.onPaySuccess)
      .catch(this.onPayError)
  }

  onPaySuccess = res => {
    const { bookData } = this.props
    const { nightsBefore, nightsAfter } = this.state

    bookData.nightsBefore = nightsBefore
    bookData.nightsAfter = nightsAfter
    this.setState(
      {
        isBusy: false,
        error: '',
        purchasedNightsBefore: nightsBefore,
        purchasedNightsAfter: nightsAfter,
        nightsBefore: 0,
        nightsAfter: 0,
      },
      () => {
        window.scrollTo(0, scrollDistanceToTop(this.refs.successFrame))
      }
    )
  }

  onPayError = error => {
    this.setState({ isBusy: false, error })
  }

  getDates = () => {
    const { trip } = this.props
    const tripData = config.trips[trip]
    const firstNight = moment(tripData.checkInDate, 'MMMM D, YYYY')
    const lastNight = moment(tripData.checkOutDate, 'MMMM D, YYYY').subtract(
      1,
      'day'
    )

    return {
      firstNight,
      lastNight,
      before: [
        moment(firstNight)
          .subtract(1, 'day')
          .format('MMM D'),
        moment(firstNight)
          .subtract(2, 'day')
          .format('MMM D'),
        moment(firstNight)
          .subtract(3, 'day')
          .format('MMM D'),
      ],
      after: [
        moment(lastNight)
          .add(1, 'day')
          .format('MMM D'),
        moment(lastNight)
          .add(2, 'day')
          .format('MMM D'),
        moment(lastNight)
          .add(3, 'day')
          .format('MMM D'),
      ],
    }
  }

  render() {
    const { isBusy, nightsBefore, nightsAfter, error } = this.state
    const { trip, bookData } = this.props
    const { room } = bookData
    const tripData = config.trips[trip]
    const roomData = tripData.rooms[room]
    const dates = this.getDates()
    const { firstNight, lastNight } = dates
    const pricePerNightInCents =
      get(roomData, 'extraNightPrices.default', 0) * 100
    const purchasedNightsBefore =
      this.state.purchasedNightsBefore || get(bookData, 'nightsBefore', 0)
    const purchasedNightsAfter =
      this.state.purchasedNightsAfter || get(bookData, 'nightsAfter', 0)
    const purchasedNightsTotal = purchasedNightsBefore + purchasedNightsAfter
    const classNames = ['add-nights']

    if (!pricePerNightInCents) return <div />
    if (purchasedNightsTotal) classNames.push('purchased')
    return (
      <div className={classNames.join(' ')}>
        {isBusy ? (
          <div className="loader-mask">
            <p>Purchasing Nights</p>
            <div className="loader" />
          </div>
        ) : (
          ''
        )}
        {purchasedNightsTotal ? (
          <div
            ref="successFrame"
            className="success"
            aria-label="Payment Success"
          >
            <div className="wrap">
              <div className="success-title">
                <h3>
                  {purchasedNightsTotal} Night{purchasedNightsTotal === 1
                    ? ''
                    : 's'}{' '}
                  Added
                </h3>
                <a className="print" onClick={window.print}>
                  Print
                  <i className="fa fa-print" />
                </a>
              </div>
              <div className="details">
                <i className="fa fa-calendar" />
                <ol>
                  {Object.keys(Array.from(Array(purchasedNightsBefore)))
                    .reverse()
                    .map(n => (
                      <li key={`night-before-${n}`}>{dates.before[n]}</li>
                    ))}
                  {Object.keys(Array.from(Array(purchasedNightsAfter))).map(
                    n => <li key={`night-after-${n}`}>{dates.after[n]}</li>
                  )}
                </ol>
              </div>
              <AddNightsSummary
                trip={trip}
                perNight={pricePerNightInCents}
                nightsBefore={purchasedNightsBefore}
                nightsAfter={purchasedNightsAfter}
                paid
              />
            </div>
          </div>
        ) : (
          <form ref="formFrame" className="wrap">
            <h2>Want to add additional Nights to your Getaway?</h2>
            <p>
              Select up to 3 nights before or up to 3 days after the getaway.
            </p>
            <div className="nights">
              <p className="preNights">
                <input
                  id="nightsBefore3"
                  type="radio"
                  name="nights[before]"
                  value={3}
                  checked={nightsBefore === 3}
                  onChange={this.onChangeNightsBefore}
                />
                <label htmlFor="nightsBefore3">
                  <time>{dates.before[2]}</time>
                  3 Nights Earlier
                </label>
                <input
                  id="nightsBefore2"
                  type="radio"
                  name="nights[before]"
                  value={2}
                  checked={nightsBefore === 2}
                  onChange={this.onChangeNightsBefore}
                />
                <label htmlFor="nightsBefore2">
                  <time>{dates.before[1]}</time>
                  2 Nights Earlier
                </label>
                <input
                  id="nightsBefore1"
                  type="radio"
                  name="nights[before]"
                  value={1}
                  checked={nightsBefore === 1}
                  onChange={this.onChangeNightsBefore}
                />
                <label htmlFor="nightsBefore1">
                  <time>{dates.before[0]}</time>
                  1 Night Earlier
                </label>
                <input
                  id="nightsBefore0"
                  type="radio"
                  name="nights[before]"
                  value={0}
                  checked={nightsBefore === 0}
                  onChange={this.onChangeNightsBefore}
                />
                <label htmlFor="nightsBefore0">
                  <time>{firstNight.format('MMM D')}</time>
                  Getaway Start
                </label>
              </p>
              <p className="postNights">
                <input
                  id="nightsAfter0"
                  type="radio"
                  name="nights[after]"
                  value={0}
                  checked={nightsAfter === 0}
                  onChange={this.onChangeNightsAfter}
                />
                <label htmlFor="nightsAfter0">
                  <time>{lastNight.format('MMM D')}</time>
                  Getaway End
                </label>
                <input
                  id="nightsAfter1"
                  type="radio"
                  name="nights[after]"
                  value={1}
                  checked={nightsAfter === 1}
                  onChange={this.onChangeNightsAfter}
                />
                <label htmlFor="nightsAfter1">
                  <time>{dates.after[0]}</time>
                  1 Night Longer
                </label>
                <input
                  id="nightsAfter2"
                  type="radio"
                  name="nights[after]"
                  value={2}
                  checked={nightsAfter === 2}
                  onChange={this.onChangeNightsAfter}
                />
                <label htmlFor="nightsAfter2">
                  <time>{dates.after[1]}</time>
                  2 Nights Longer
                </label>
                <input
                  id="nightsAfter3"
                  type="radio"
                  name="nights[after]"
                  value={3}
                  checked={nightsAfter === 3}
                  onChange={this.onChangeNightsAfter}
                />
                <label htmlFor="nightsAfter3">
                  <time>{dates.after[2]}</time>
                  3 Nights Longer
                </label>
              </p>
            </div>
            <AddNightsSummary
              trip={trip}
              perNight={pricePerNightInCents}
              nightsBefore={nightsBefore}
              nightsAfter={nightsAfter}
            />
            <fieldset
              style={{
                height: nightsBefore || nightsAfter ? 'initial' : 0,
                visibility: nightsBefore || nightsAfter ? 'visible' : 'hidden',
              }}
            >
              <h2>Credit Card</h2>
              <p className="field card-wrap">
                <span id="card-element" />
              </p>
            </fieldset>
            {error ? <p className="error">{error}</p> : ''}
            {nightsBefore || nightsAfter ? (
              <p className="button-bar">
                <button className="btn-primary" onClick={this.onSubmit}>
                  Purchase Nights
                </button>
              </p>
            ) : (
              ''
            )}
          </form>
        )}
      </div>
    )
  }
}



// WEBPACK FOOTER //
// ./src/components/partials/add-nights.js